import { useCallback, useEffect, useMemo, useState } from 'react';
import { DiscoveryAnswer, DiscoveryFormSection } from '../../../types/advise';
import { fetchAdviseDiscovery, updateAdviseDiscoveryAnswer } from '../../../Api/Advise/Engagements';
import { addError } from '../../../store/Error';
import { EngagementDiscovery } from '../../Compounds/Advise/SideBySideView';
import { usePendingSaves } from './usePendingSaves';

type DebouncedSave = ReturnType<typeof usePendingSaves>['debouncedSave'];

/** The engagement's Discovery form, loaded on open, and how to answer it with each answer saved in the background. */
export const useEngagementDiscovery = (
  engagementId: string | null,
  canEdit: boolean,
  debouncedSave: DebouncedSave
): EngagementDiscovery => {
  const [sections, setSections] = useState<DiscoveryFormSection[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setSections(null);
    setFailed(false);
    if (!engagementId) return;
    let current = true;
    const loadDiscovery = async () => {
      const { data, error } = await fetchAdviseDiscovery(engagementId);
      if (!current) return;
      if (error || !data) {
        addError('advise-engagement', error?.message);
        setFailed(true);
      } else {
        setSections(data.sections);
      }
    };
    loadDiscovery();
    return () => {
      current = false;
    };
  }, [engagementId, attempt]);

  const answer = useCallback(
    (questionId: string, value: DiscoveryAnswer) => {
      if (!canEdit || !engagementId) return;
      setSections((prev) =>
        prev
          ? prev.map((section) => ({
              ...section,
              questions: section.questions.map((question) =>
                question.id === questionId ? { ...question, value } : question
              )
            }))
          : prev
      );
      debouncedSave(`discovery:${questionId}`, () => updateAdviseDiscoveryAnswer(engagementId, questionId, value));
    },
    [canEdit, engagementId, debouncedSave]
  );

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  return useMemo(() => ({ sections, failed, retry, answer }), [sections, failed, retry, answer]);
};
